export type SortDir = "asc" | "desc";
export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}
export type SortValue = string | number | null | undefined;

/** Empties (null/undefined/""/NaN) are NOT handled here; sortRows puts them last. Numbers before strings. */
export function compareSortValues(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "number") return -1;
  if (typeof b === "number") return 1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

function isEmpty(v: SortValue): v is null | undefined | "" {
  return v == null || v === "" || (typeof v === "number" && Number.isNaN(v));
}

/** New array; stable; empties last in both directions. */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  value: (row: T, key: K) => SortValue,
  state: SortState<K>,
): T[] {
  const sign = state.dir === "asc" ? 1 : -1;
  const keyed = rows.map((row, index) => ({ row, index, v: value(row, state.key) }));
  keyed.sort((x, y) => {
    const xe = isEmpty(x.v);
    const ye = isEmpty(y.v);
    if (xe && ye) return x.index - y.index;
    if (xe) return 1;
    if (ye) return -1;
    const c = compareSortValues(x.v as string | number, y.v as string | number) * sign;
    return c !== 0 ? c : x.index - y.index;
  });
  return keyed.map((k) => k.row);
}

/** Same key toggles dir; a different key starts at initialDir. */
export function nextSortState<K extends string>(
  current: SortState<K>,
  clicked: K,
  initialDir: SortDir,
): SortState<K> {
  if (current.key === clicked) return { key: clicked, dir: current.dir === "asc" ? "desc" : "asc" };
  return { key: clicked, dir: initialDir };
}
